import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faVolumeDown,
  faTimesCircle,
  faBan,
  faCalendarCheck,
  faPhoneSlash,
  faPhoneAlt,
  faInfoCircle,
  faCalendarAlt,
  faUser,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const InterviewLineDetail = () => {
  const { uploadId, lineId } = useParams();
  const navigate = useNavigate();
  const [line, setLine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLineDetail();
  }, [uploadId, lineId]);

  const fetchLineDetail = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching line detail:', { uploadId, lineId });
      const response = await api.get(`/cati/interview-uploads/${uploadId}/lines/${lineId}`);
      console.log('Line detail response:', response.data);
      
      if (response.data.success) {
        setLine(response.data.data);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Error fetching line details:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Erreur lors du chargement de la ligne');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getResultStatusBadge = (status) => {
    const statusConfig = {
      'ok': { bg: 'bg-green-100', text: 'text-green-800', label: 'OK', icon: faCheckCircle },
      'boite_vocal': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Boîte Vocale', icon: faVolumeDown },
      'refus': { bg: 'bg-red-100', text: 'text-red-800', label: 'Refus', icon: faTimesCircle },
      'hors_cible': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Hors Cible', icon: faBan },
      'rendez_vous': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Rendez-vous', icon: faCalendarCheck },
      'injoignable': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Injoignable', icon: faPhoneSlash },
      'faux_numero': { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Faux Numéro', icon: faPhoneAlt }
    };
    
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          En attente
        </span>
      );
    }
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: faInfoCircle };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-6xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (error || !line) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error || 'Ligne non trouvée'}</p>
          <button
            onClick={() => navigate(`/cati/interview-uploads/${uploadId}`)}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Ligne #{line.line_index + 1}</h1>
                <p className="text-gray-600 mt-1">Détails de la ligne d'interview</p>
              </div>
              <div>
                {getResultStatusBadge(line.result_status)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Line Data */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Données de la Ligne</h2>
              <div className="space-y-4">
                {Object.entries(line.line_data || {}).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-200 pb-3">
                    <p className="text-sm font-medium text-gray-500 mb-1 uppercase">{key}</p>
                    <p className="text-base text-gray-900">{value || '-'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Interview Results */}
            <div className="space-y-6">
              {/* Upload Info */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Informations Upload</h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Fichier</p>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faFileAlt} className="text-gray-400 mr-2" />
                      <p className="text-base font-medium text-gray-900">
                        {line.upload?.file_name || '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Enquête</p>
                    <p className="text-base font-medium text-gray-900">
                      {line.upload?.survey?.title || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Enquêteur</p>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                      <p className="text-base font-medium text-gray-900">
                        {line.interviewer?.name || '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Code Agence</p>
                    <p className="text-base font-medium text-gray-900">
                      {line.code_agence || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Result Info */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Résultat du Questionnaire</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Statut</p>
                    {getResultStatusBadge(line.result_status)}
                  </div>

                  {line.comment && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Commentaire</p>
                      <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {line.comment}
                      </p>
                    </div>
                  )}

                  {line.reported_rendez_vous && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Rendez-vous</p>
                      <div className="flex items-center text-base text-gray-900">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 mr-2" />
                        {formatDate(line.reported_rendez_vous)}
                      </div>
                    </div>
                  )}

                  {line.completed_at && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Complété le</p>
                      <div className="flex items-center text-base text-gray-900">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 mr-2" />
                        {formatDate(line.completed_at)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewLineDetail;

